<script lang="ts">
  import CalendarIcon from '@lucide/svelte/icons/calendar';
  import ArrowRightIcon from '@lucide/svelte/icons/arrow-right';
  import type { DateValue } from '@internationalized/date';
  import { Button } from '$lib/components/ui/button';
  import { RangeCalendar } from '$lib/components/ui/range-calendar';
  import * as Popover from '$lib/components/ui/popover';
  import { cn } from '$lib/utils';
  import { addNights, formatDate, nightsBetween } from '$lib/dates';

  type DateRange = { start: DateValue | undefined; end: DateValue | undefined };

  type Props = {
    checkin: DateValue;
    checkout: DateValue;
    onValueChange: (range: { start: DateValue; end: DateValue }) => void;
    checkinLabel: string;
    checkoutLabel: string;
    minValue?: DateValue;
    locale?: string;
    portalTarget?: HTMLElement | null;
  };

  let {
    checkin,
    checkout,
    onValueChange,
    checkinLabel,
    checkoutLabel,
    minValue,
    locale = 'fr-FR',
    portalTarget = null,
  }: Props = $props();

  let open = $state(false);
  let numberOfMonths = $state(1);

  $effect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => {
      numberOfMonths = mq.matches ? 2 : 1;
    };
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  });

  const value = $derived<DateRange>({ start: checkin, end: checkout });

  function handleChange(range: DateRange | undefined) {
    if (!range || !range.start) return;
    if (!range.end) return;
    const end =
      nightsBetween(range.start, range.end) >= 1
        ? range.end
        : addNights(range.start, 1);
    onValueChange({ start: range.start, end });
    open = false;
  }
</script>

<Popover.Root bind:open>
  <Popover.Trigger>
    {#snippet child({ props })}
      <Button
        {...props}
        variant="outline"
        class={cn(
          'qs-daterangepicker flex h-auto min-h-[42px] w-full flex-row items-center gap-2 px-2.5 py-1.5 text-left text-sm font-normal',
        )}
      >
        <span
          class="qs-daterangepicker-segment flex min-w-0 flex-1 flex-col items-start leading-tight"
        >
          <span
            class="qs-daterangepicker-label w-full truncate text-[10px] font-medium uppercase tracking-wide text-primary"
          >
            {checkinLabel}
          </span>
          <span
            class="qs-daterangepicker-date w-full truncate text-sm font-medium"
          >
            {formatDate(checkin, locale)}
          </span>
        </span>
        <ArrowRightIcon
          class="qs-daterangepicker-arrow h-3.5 w-3.5 shrink-0 text-muted-foreground"
        />
        <span
          class="qs-daterangepicker-segment flex min-w-0 flex-1 flex-col items-start leading-tight"
        >
          <span
            class="qs-daterangepicker-label w-full truncate text-[10px] font-medium uppercase tracking-wide text-primary"
          >
            {checkoutLabel}
          </span>
          <span
            class="qs-daterangepicker-date w-full truncate text-sm font-medium"
          >
            {formatDate(checkout, locale)}
          </span>
        </span>
        <CalendarIcon
          class="qs-daterangepicker-icon h-3.5 w-3.5 shrink-0 text-primary"
        />
      </Button>
    {/snippet}
  </Popover.Trigger>
  <Popover.Content
    class="qs-daterangepicker-popover w-auto p-0"
    align="start"
    portalProps={portalTarget ? { to: portalTarget } : undefined}
  >
    <RangeCalendar
      {value}
      onValueChange={handleChange}
      {minValue}
      {locale}
      {numberOfMonths}
      pagedNavigation
    />
  </Popover.Content>
</Popover.Root>
